import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tag, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CURRENCY_SYMBOL, EGYPT_VAT_RATE, breakdownInclusive, formatCurrency } from "@/lib/currency";
import type { PlanPriceRow } from "@/hooks/usePlanPrices";

const PLAN_LABELS: Record<string, { name: string; subtitle: string; color: string }> = {
  basic: { name: "النظام الإداري", subtitle: "Starter 🟢", color: "border-green-500" },
  standard: { name: "إداري + تعليمي", subtitle: "Growth 🟡", color: "border-blue-500" },
  premium: { name: "النظام الكامل", subtitle: "Pro 🔵", color: "border-amber-500" },
};

const AccountantPlanPricing = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Record<string, Partial<PlanPriceRow>>>({});

  const { data: plans, isLoading } = useQuery({
    queryKey: ["plan-prices-admin", "EGP"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_plan_prices" as any)
        .select("*")
        .eq("currency", "EGP")
        .order("monthly_price", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as PlanPriceRow[];
    },
  });

  // Initialize editing state once plans load
  useEffect(() => {
    if (plans?.length && Object.keys(editing).length === 0) {
      const init: Record<string, Partial<PlanPriceRow>> = {};
      plans.forEach((p) => (init[p.id] = { ...p }));
      setEditing(init);
    }
  }, [plans]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async (row: Partial<PlanPriceRow> & { id: string }) => {
      const { error } = await supabase
        .from("subscription_plan_prices" as any)
        .update({
          monthly_price: Number(row.monthly_price) || 0,
          yearly_price: row.yearly_price ? Number(row.yearly_price) : null,
          per_child_price: row.per_child_price ? Number(row.per_child_price) : null,
          active: !!row.active,
          notes: row.notes || null,
        })
        .eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم حفظ السعر");
      qc.invalidateQueries({ queryKey: ["plan-prices-admin", "EGP"] });
      qc.invalidateQueries({ queryKey: ["plan-prices", "EGP"] });
    },
    onError: (e: any) => toast.error(e?.message || "فشل الحفظ"),
  });

  const update = (id: string, patch: Partial<PlanPriceRow>) =>
    setEditing((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Tag className="w-6 h-6 text-primary" />
          أسعار باقات الاشتراك (EGP)
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          تحكم في أسعار الباقات بالجنيه المصري — يتم احتساب ضريبة القيمة المضافة المصرية {EGYPT_VAT_RATE}% تلقائياً عند إصدار الفواتير.
        </p>
      </div>

      {isLoading ? (
        <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans?.map((plan) => {
            const meta = PLAN_LABELS[plan.plan_id] || { name: plan.plan_id, subtitle: "", color: "border-primary" };
            const e = editing[plan.id] || plan;
            const monthly = Number(e.monthly_price) || 0;
            const yearly = Number(e.yearly_price) || 0;
            const monthlyVat = breakdownInclusive(monthly);
            const yearlyVat = breakdownInclusive(yearly);

            return (
              <Card key={plan.id} className={`border-r-4 ${meta.color}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{meta.name}</span>
                    <Badge variant="outline" className="text-xs">{meta.subtitle}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">السعر الشهري ({CURRENCY_SYMBOL}) — شامل الضريبة</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={e.monthly_price ?? ""}
                      onChange={(ev) => update(plan.id, { monthly_price: Number(ev.target.value) })}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      صافي: {formatCurrency(monthlyVat.subtotal)} • ضريبة {EGYPT_VAT_RATE}%: {formatCurrency(monthlyVat.vatAmount)}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">السعر السنوي ({CURRENCY_SYMBOL}) — شامل الضريبة</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={e.yearly_price ?? ""}
                      onChange={(ev) => update(plan.id, { yearly_price: Number(ev.target.value) })}
                    />
                    <p className="text-[11px] text-muted-foreground">
                      صافي: {formatCurrency(yearlyVat.subtotal)} • ضريبة {EGYPT_VAT_RATE}%: {formatCurrency(yearlyVat.vatAmount)}
                    </p>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">سعر/طفل (اختياري)</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={e.per_child_price ?? ""}
                      onChange={(ev) =>
                        update(plan.id, { per_child_price: ev.target.value === "" ? null : Number(ev.target.value) })
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t">
                    <Label className="text-xs">مفعّلة للحضانات</Label>
                    <Switch
                      checked={!!e.active}
                      onCheckedChange={(v) => update(plan.id, { active: v })}
                    />
                  </div>

                  <Button
                    className="w-full gap-2"
                    onClick={() => save.mutate({ id: plan.id, ...e })}
                    disabled={save.isPending}
                  >
                    {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    حفظ
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountantPlanPricing;
